package com.arena_web.ui.components.events

import androidx.compose.runtime.Composable
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

/**
 * Generic confirm dialog used by the live-match action buttons
 * (Reset Timer, End Match). Mirrors `DeleteEventConfirmDialog` shape so
 * the modal language reads consistently across event admin actions.
 */
@Composable
fun MatchActionConfirmDialog(
    isOpen: Boolean,
    title: String,
    message: String,
    confirmText: String,
    iconSrc: String,
    iconAccent: String,
    isBusy: Boolean,
    busyText: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { if (!isBusy) onCancel() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(420)
                // width:100% ignores the margins inside the grid overlay and the
                // panel runs edge-to-edge on phones — subtract them instead.
                .styleModifier { property("width", "calc(100% - ${s(32)})") }
                .sMargin(16)
                .sBorderRadius(26)
                .sPadding(28, 26)
                .sGap(20)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("background-color", "#09142D")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                    property("border", "0.5px solid rgba(255, 255, 255, 0.06)")
                }
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(14),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("background-color", "${iconAccent}24")
                            property("border", "1px solid ${iconAccent}59")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    // Mask-image paints the SVG glyph in `iconAccent` so the
                    // icon reads as a colored mark rather than the asset's
                    // native fill (which would render washed out against the
                    // tinted bubble).
                    Box(
                        modifier = Modifier
                            .sSize(20)
                            .styleModifier {
                                property("background-color", iconAccent)
                                property("mask-image", "url('$iconSrc')")
                                property("-webkit-mask-image", "url('$iconSrc')")
                                property("mask-size", "contain")
                                property("-webkit-mask-size", "contain")
                                property("mask-position", "center")
                                property("-webkit-mask-position", "center")
                                property("mask-repeat", "no-repeat")
                                property("-webkit-mask-repeat", "no-repeat")
                            }
                    )
                }
                SpanText(
                    title,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Secondary)
                        .styleModifier {
                            property("font-family", "Urbanist, sans-serif")
                            property("text-align", "center")
                        }
                )
            }

            SpanText(
                message,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(15)
                    .color(Color("#BED6F5"))
                    .styleModifier {
                        property("font-family", "Urbanist, sans-serif")
                        property("line-height", "1.5")
                        property("text-align", "center")
                    }
            )

            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                DialogPillButton(
                    text = t.commonCancel,
                    onClick = { if (!isBusy) onCancel() },
                    background = AppGradients.InteractiveSecondary,
                    textColor = "#BED6F5",
                    enabled = !isBusy,
                )
                DialogPillButton(
                    text = if (isBusy) busyText else confirmText,
                    onClick = { if (!isBusy) onConfirm() },
                    background = "#0061FF",
                    textColor = "#FFFFFF",
                    enabled = !isBusy,
                )
            }
        }
    }
}
